import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { useQuery, useMutation } from '@apollo/client';
import { graphql } from '../../../__generated__';
import { UserSearchResultFragment } from '../../../__generated__/graphql';
import { Grid, LinearProgress, Typography } from '@mui/material';
import { User } from '../../../types';
import { pollServer } from '../../../graphql/utils';
import { fixToPhoneNumber, validatePhone } from 'gmd-utilities-package';
import FormDialogContainer, {
  DialogBody,
} from '../../../components/common/FormDialogContainer';

export const UserSearch_UserFragment = graphql(/* GraphQL */ `
  fragment UserSearchResult on RegisteredUser {
    id
    name
    phone
    sender
  }
`);

export const userSearchQuery = graphql(/* GraphQL */ `
  query GetUsersMatchingText(
    $sender: String
    $searchText: String
    $pageSize: Int
  ) {
    users(sender: $sender, searchText: $searchText, pageSize: $pageSize) {
      ...UserSearchResult
    }
  }
`);

export const verifyPhoneQuery = graphql(/* GraphQL */ `
  query VerifyPhone($phoneNumber: String!) {
    verifyPhone(phoneNumber: $phoneNumber) {
      status
      name
      phone
    }
  }
`);

export const userByIdQuery = graphql(/* GraphQL */ `
  query GetUserById($id: ID!) {
    user(id: $id) {
      ...UserSearchResult
    }
  }
`);

export const addUserMutation = graphql(/* GraphQL */ `
  mutation AddUser($sender: String!, $name: String!, $phone: String!) {
    addUser(sender: $sender, name: $name, phone: $phone) {
      success
      code
      message
      user {
        id
        name
        phone
        sender
      }
    }
  }
`);

export const updateUserMutation = graphql(/* GraphQL */ `
  mutation UpdateUser($id: ID!, $name: String!, $phone: String!) {
    updateUser(id: $id, name: $name, phone: $phone) {
      success
      code
      message
      user {
        id
        name
        phone
      }
    }
  }
`);

export const transactionCountQuery = graphql(/* GraphQL */ `
  query CountTransactions($senderId: String!, $recipientId: String!) {
    countTransactions(senderId: $senderId, recipientId: $recipientId)
  }
`);

// const filter = createFilterOptions<UserSearchType>();

export type UserSearchProps = {
  type: string;
  sender?: string;
  srcCurrency: string;
  destCurrency: string;
  user?: User | null;
  label: string;
  onUserChanged: (user: User | null) => void;
  disabled?: boolean;
};

export type DialogValue = {
  id: string;
  name: string;
  phone: string;
};

const UserSearch = (props: UserSearchProps) => {
  const { user, type, sender, srcCurrency, destCurrency } = props;
  const [canVerifyPhone, setCanVerifyPhone] = React.useState<boolean>(false);
  const [text, setText] = React.useState('');
  const [value, setValue] = React.useState<UserSearchResultFragment | null>(
    user || null
  );
  const [open, toggleOpen] = React.useState(false);
  const [addError, setAddError] = React.useState('');
  const [dialogMode, setDialogMode] = React.useState('add');

  const dialogValueReducer = (state: DialogValue, action: DialogValue) => {
    let phone = action.phone;
    if (phone && type !== 'sender') {
      phone = fixToPhoneNumber(phone, destCurrency);
      const error = validatePhone(phone, destCurrency);
      setCanVerifyPhone(!error && state.phone !== action.phone && !action.name);
    }
    return {
      ...state,
      id: action.id,
      name: action.name,
      phone: phone,
    };
  };
  const [dialogValue, setDialogValue] = React.useReducer(dialogValueReducer, {
    id: user?.id || '',
    name: user?.name || '',
    phone: user?.phone || '',
  });

  React.useEffect(() => {
    const error = validatePhone(
      dialogValue.phone,
      type === 'sender' ? srcCurrency : destCurrency
    );
    setAddError(error);
  }, [dialogValue.phone, sender, srcCurrency, destCurrency, setAddError, type]);

  const {
    data: searchResults,
    loading: searching,
    error: searchError,
    startPolling,
    stopPolling,
  } = useQuery(userSearchQuery, {
    variables: sender
      ? { sender, searchText: text, pageSize: 20 }
      : { searchText: text, pageSize: 20 },
    skip: type === 'recipient' && !sender,
  });

  const { data: verifyPhoneResult, loading: verifyPhoneResultLoading } =
    useQuery(verifyPhoneQuery, {
      variables: {
        phoneNumber: fixToPhoneNumber(dialogValue.phone, destCurrency),
      },
      skip: !(canVerifyPhone && type === 'recipient' && sender),
    });

  React.useEffect(() => {
    if (
      verifyPhoneResult &&
      verifyPhoneResult.verifyPhone &&
      (verifyPhoneResult.verifyPhone.status === 'ACTIVE' ||
        verifyPhoneResult.verifyPhone.status === 'VERIFIED')
    ) {
      setDialogValue({
        id: dialogValue.id,
        phone: verifyPhoneResult.verifyPhone.phone || '',
        name: verifyPhoneResult.verifyPhone.name || '',
      });
    }
  }, [verifyPhoneResult, dialogValue.id]);

  const {
    data: transactionCount,
    loading: countingTransactions,
    error: countingTransactionsError,
  } = useQuery(transactionCountQuery, {
    variables: { senderId: sender || '', recipientId: dialogValue.id },
    skip: !sender || !dialogValue.id,
  });

  const [addUser, { loading: addUserLoading, error: addUserError }] =
    useMutation(addUserMutation);
  const [updateUser, { loading: updateUserLoading, error: updateUserError }] =
    useMutation(updateUserMutation);

  React.useEffect(() => {
    // if (value !== null) {
    props.onUserChanged(value);
    // }
  }, [value, props]);

  const handleClose = () => {
    pollServer(startPolling, stopPolling);
    setAddError('');
    setDialogValue({
      id: '',
      name: '',
      phone: '',
    });
    toggleOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddError('');
    if (dialogMode === 'add') {
      const variables = {
        sender: sender || '',
        name: dialogValue.name,
        phone: dialogValue.phone,
      };
      addUser({ variables }).then((result) => {
        const data = result.data?.addUser;
        if (data?.success === false) {
          setAddError(data.message);
        } else if (data && data.user) {
          setValue({
            id: data.user.id,
            name: data.user.name,
            phone: data.user.phone,
          });
          handleClose();
        }
      });
    } else {
      const variables = {
        id: dialogValue.id,
        name: dialogValue.name,
        phone: dialogValue.phone,
      };
      updateUser({ variables }).then((result) => {
        const data = result.data?.updateUser;
        if (data?.success === false) {
          setAddError(data.message);
        } else if (data && data.user) {
          setValue({
            id: data.user.id,
            name: data.user.name,
            phone: data.user.phone,
          });
          handleClose();
        }
      });
    }
  };

  let options: User[] = [];
  const tempAddUser: User = { id: 'add', name: '', phone: '' };
  const tempEditUser: User = { id: 'edit', name: '', phone: '' };
  if (searchResults?.users && searchResults.users.length > 0) {
    options = [...(searchResults.users as User[])];
  }
  // can edit if something is selected and either this is a sender or a recipient with no current transactions
  // Should we allow changing recipient name only and not number after they have a transaction in system?
  if (
    value !== null &&
    (!sender || transactionCount?.countTransactions === 0)
  ) {
    options.unshift(tempEditUser);
    options.unshift(tempAddUser);
  } else {
    options.unshift(tempAddUser);
  }
  const formError =
    addUserError?.message ||
    updateUserError?.message ||
    searchError?.message ||
    countingTransactionsError?.message;

  return (
    <React.Fragment>
      <Autocomplete
        value={value}
        onChange={(event, newValue) => {
          if (newValue === null) {
            setValue(null);
            return;
          }
          if (typeof newValue === 'string') {
            setText(newValue);
          } else if (newValue.id === 'add') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              setDialogMode('add');
              setDialogValue({
                id: '',
                name: '',
                phone: '',
              });
              toggleOpen(true);
            });
          } else if (newValue.id === 'edit') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              //handle close sets the dialog value of the form to null
              if (value) {
                setDialogValue(value);
              }
              setDialogMode('edit');
              toggleOpen(true);
            });
          } else {
            setValue(newValue);
            setDialogValue(newValue);
          }
        }}
        // filterOptions={(options, params) => {
        //   const filtered = filter(options, params);

        //   if (params.inputValue !== '') {
        //     filtered.push({
        //       inputValue: params.inputValue,
        //       title: `Add "${params.inputValue}"`,
        //     });
        //   }

        //   return filtered;
        // }}
        id="user-search"
        options={options}
        getOptionLabel={(option) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          // if (option.inputValue) {
          //   return option.inputValue;
          // }
          if (option.id === 'add') {
            return 'Add User';
          }
          if (option.id === 'edit') {
            return 'Edit Selected User';
          }
          return `${option.name} (${option.phone})`;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        // renderOption={(props, option) => <li {...props}>{option.name}</li>}
        sx={{ mt: 1 }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.label}
            helperText={formError || ''}
            error={!!formError}
          />
        )}
        disabled={
          searching ||
          props.disabled ||
          addUserLoading ||
          updateUserLoading ||
          countingTransactions ||
          (type === 'recipient' && !sender)
        }
      />
      {(addUserLoading || updateUserLoading) && <LinearProgress />}
      <FormDialogContainer>
        <DialogBody
          open={open}
          loading={
            addUserLoading || updateUserLoading || verifyPhoneResultLoading
          }
          disabled={
            dialogValue.name.length === 0 || dialogValue.phone.length === 0
          }
          title={
            dialogMode === 'add' ? 'Add a new contact' : 'Edit selected contact'
          }
          contextTitle="Enter the contact's name and phone number"
          onClose={handleClose}
          onSubmitAsync={handleSubmit}
        >
          <Grid container>
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoFocus
                id="new_user_phone"
                value={dialogValue.phone}
                onChange={(event) => {
                  setDialogValue({
                    ...dialogValue,
                    phone: fixToPhoneNumber(
                      event.target.value,
                      (sender && sender.length > 0
                        ? srcCurrency
                        : destCurrency
                      ).toUpperCase()
                    ),
                  });
                }}
                label="Phone number"
                type="text"
                variant="standard"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="new_user_name"
                value={dialogValue.name}
                onChange={(event) => {
                  setDialogValue({
                    ...dialogValue,
                    name: event.target.value,
                  });
                }}
                label="Name"
                type="text"
                variant="standard"
              />
            </Grid>
            {addError && addError.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  sx={{ color: 'red', fontWeight: 'normal' }}
                >
                  {addError}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogBody>
      </FormDialogContainer>
    </React.Fragment>
  );
};

export default UserSearch;
